import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";

/* ---------------------------------------------------------
   Design tokens — kept in sync with Dashboard.jsx /
   SitemapGroups.jsx. Worth promoting to a shared theme file
   once a third or fourth screen needs it.
--------------------------------------------------------- */
const COLOR = {
  bg: "#F5F6F4",
  surface: "#FFFFFF",
  ink: "#161A1D",
  inkMuted: "#6B7280",
  inkFaint: "#9AA1A9",
  line: "#E4E7E7",
  accent: "#0B7285",
};
const FONT_MONO = "'JetBrains Mono', monospace";

/* Centralized so a wrong guess about a route name is a
   one-line fix rather than a hunt through the component. */
const ENDPOINTS = {
  groups: (source) => `/api/v1/sitemap/groups?source=${source}`,
  create: "/api/v1/sitemap/add-custom-urls",
  // Confirmed route: PUT /url/:id (mounted under /api/v1/sitemap).
  update: (id) => `/api/v1/sitemap/url/${id}`,
};

const PRIORITY_OPTIONS = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
const CHANGEFREQ_OPTIONS = [
  "always",
  "hourly",
  "daily",
  "weekly",
  "monthly",
  "yearly",
  "never",
];

const DEFAULT_FORM = {
  group_id: "",
  url: "",
  priority: 0.5,
  changefreq: "weekly",
};

function isValidUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function validateUrlForm(form, hasGroups) {
  const errors = {};

  if (!form.url.trim()) {
    errors.url = "URL is required.";
  } else if (!isValidUrl(form.url.trim())) {
    errors.url = "Must be a full URL starting with http:// or https://.";
  }

  if (hasGroups && !form.group_id) {
    errors.group_id = "Choose a sitemap group.";
  }

  if (!form.priority && form.priority !== 0) {
    errors.priority = "Priority is required.";
  }

  if (!form.changefreq) {
    errors.changefreq = "Change frequency is required.";
  }

  return errors;
}

export default function AddUrlForm({ source, editingUrl, onBack, onClose, onSuccess }) {
  const isEditing = Boolean(editingUrl);

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(() =>
    isEditing
      ? {
          group_id: editingUrl.group_id ?? "",
          url: editingUrl.url ?? "",
          priority: editingUrl.priority ?? 0.5,
          changefreq: editingUrl.changefreq ?? "weekly",
          type: editingUrl.type ?? "",
        }
      : DEFAULT_FORM
  );
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;

    const loadGroups = async () => {
      setLoadingGroups(true);
      try {
        const { data } = await axios.get(ENDPOINTS.groups(source));
        if (!cancelled) setGroups(data.data || []);
      } catch (error) {
        if (!cancelled) {
          toast.error(
            error.response?.data?.message || "Failed to load sitemap groups."
          );
        }
      } finally {
        if (!cancelled) setLoadingGroups(false);
      }
    };

    loadGroups();
    return () => {
      cancelled = true;
    };
  }, [source]);

  const hasGroups = groups.length > 0;

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  // A group can be "full" from a previous selection load, but the
  // group currently assigned to this URL (when editing) should
  // always remain selectable even if it's since filled up.
  const selectableGroups = useMemo(
    () =>
      groups.map((g) => ({
        ...g,
        disabled: g.is_full && g.id !== editingUrl?.group_id,
      })),
    [groups, editingUrl?.group_id]
  );

  const save = async () => {
    const validationErrors = validateUrlForm(form, hasGroups);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const payload = { ...form, url: form.url.trim(), source };

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(ENDPOINTS.update(editingUrl.id), payload);
        toast.success("URL updated successfully.");
      } else {
        await axios.post(ENDPOINTS.create, payload);
        toast.success("URL added successfully.");
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (isEditing ? "Failed to update URL." : "Failed to add URL.")
      );
    } finally {
      setSaving(false);
    }
  };

  const title = isEditing
    ? "Edit URL"
    : source === "wordpress"
    ? "Add WordPress URL"
    : "Add custom URL";

  return (
    <>
      <DialogTitle sx={{ fontWeight: 600, color: COLOR.ink }}>{title}</DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {!loadingGroups && !hasGroups && (
            <Alert severity="warning" sx={{ borderRadius: "8px" }}>
              No sitemap groups exist for this source yet. Create a group first,
              then add URLs to it.
            </Alert>
          )}

          <TextField
            select
            fullWidth
            label="Sitemap group"
            value={form.group_id}
            onChange={(e) => updateField("group_id", e.target.value)}
            error={!!errors.group_id}
            helperText={errors.group_id}
            disabled={loadingGroups || !hasGroups || saving}
            SelectProps={{
              displayEmpty: true,
              renderValue: (value) => {
                if (loadingGroups) return "Loading groups…";
                if (!value) return "Select a group";
                const match = groups.find((g) => g.id === value);
                return match ? `${match.name} (${match.url_count}/${match.chunk_size})` : value;
              },
            }}
          >
            {selectableGroups.map((g) => (
              <MenuItem key={g.id} value={g.id} disabled={g.disabled}>
                {g.name} ({g.url_count}/{g.chunk_size})
                {g.disabled ? " — full" : ""}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="URL"
            placeholder="https://example.com/page"
            value={form.url}
            onChange={(e) => updateField("url", e.target.value)}
            error={!!errors.url}
            helperText={errors.url}
            disabled={saving}
            InputProps={{
              startAdornment: (
                <LinkOutlinedIcon sx={{ fontSize: 18, color: COLOR.inkFaint, mr: 1 }} />
              ),
            }}
          />

          <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
            <TextField
              select
              fullWidth
              label="Priority"
              value={form.priority}
              onChange={(e) => updateField("priority", Number(e.target.value))}
              error={!!errors.priority}
              helperText={errors.priority}
              disabled={saving}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p.toFixed(1)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              label="Change frequency"
              value={form.changefreq}
              onChange={(e) => updateField("changefreq", e.target.value)}
              error={!!errors.changefreq}
              helperText={errors.changefreq}
              disabled={saving}
            >
              {CHANGEFREQ_OPTIONS.map((f) => (
                <MenuItem key={f} value={f} sx={{ textTransform: "capitalize" }}>
                  {f}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {form.url && isValidUrl(form.url.trim()) && (
            <Box
              sx={{
                bgcolor: COLOR.bg,
                border: `1px solid ${COLOR.line}`,
                borderRadius: "8px",
                p: "10px 14px",
              }}
            >
              <Typography sx={{ fontSize: "11px", color: COLOR.inkMuted }}>
                Will be listed as
              </Typography>
              <Typography sx={{ fontFamily: FONT_MONO, fontSize: "13px", color: COLOR.ink, wordBreak: "break-all" }}>
                {form.url.trim()}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {!isEditing && (
          <Button onClick={onBack} disabled={saving} sx={{ textTransform: "none", color: COLOR.inkMuted }}>
            Back
          </Button>
        )}
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", color: COLOR.inkMuted }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={save}
          disabled={saving || (!loadingGroups && !hasGroups)}
          sx={{
            bgcolor: COLOR.accent,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { bgcolor: "#095e6b" },
          }}
        >
          {saving ? (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          ) : isEditing ? (
            "Save changes"
          ) : (
            "Save URL"
          )}
        </Button>
      </DialogActions>
    </>
  );
}